import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronRight, FaTimes } from "react-icons/fa";
import Paginations from "../GenericComponents/Pagination";
import { fetchProviderInsightsApplications } from "./providerInsightsApi";
import {
  TABLE_CELL_CLASS,
  TABLE_HEAD_CLASS,
  TruncateTooltip,
  formatDate,
} from "./providerInsightsShared";
import ProviderInsightsApplicationStatus from "./ProviderInsightsApplicationStatus";

const FILTER_BTN_RESET = {
  border: "none",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
  boxShadow: "none",
};

export default function ProviderInsightsApplicationsModal({
  open,
  onClose,
  title,
  providerId,
  segment,
  intakeIds,
  location,
  searchTerm,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });

  const load = useCallback(
    async (page = 1, perPage = 10) => {
      if (!open || !providerId || !segment) return;
      setLoading(true);
      setError("");
      try {
        const result = await fetchProviderInsightsApplications({
          segment,
          providerId,
          intakeIds,
          page,
          perPage,
          location: location || undefined,
          searchTerm: searchTerm || undefined,
        });
        setRows(result.data);
        setPagination(result.pagination);
      } catch (err) {
        setError(err?.message || "Unable to load applications.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [open, providerId, segment, intakeIds, location, searchTerm],
  );

  useEffect(() => {
    if (open) {
      load(1);
    } else {
      setRows([]);
      setError("");
      setPagination((p) => ({ ...p, currentPage: 1, total: 0 }));
    }
  }, [open, providerId, segment, intakeIds, load]);

  const handlePageChange = (page, newPerPage) => {
    const per = newPerPage ? parseInt(newPerPage, 10) : pagination.perPage;
    const nextPage = newPerPage ? 1 : page;
    load(nextPage, per);
  };

  if (!open) return null;

  const showCoeColumn =
    segment === "reached_coe" || segment === "reached_coe_discontinued";
  const colSpan = showCoeColumn ? 7 : 6;

  return (
    <>
      <div
        className="cms-app-modal-backdrop fixed inset-0 z-[1040] bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="provider-insights-apps-modal-title"
      >
        <div
          className="tailwind-container flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#E5E7EB] px-4 py-3">
            <h2 id="provider-insights-apps-modal-title" className="text-[15px] font-bold text-[#35314d]">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              style={FILTER_BTN_RESET}
              aria-label="Close"
            >
              <FaTimes size={14} />
            </button>
          </div>

          {error ? (
            <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
              {error}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-auto px-2 py-2">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr>
                  <th className={TABLE_HEAD_CLASS}>#</th>
                  <th className={TABLE_HEAD_CLASS}>Student</th>
                  <th className={TABLE_HEAD_CLASS}>Intake</th>
                  <th className={TABLE_HEAD_CLASS}>Enrollment</th>
                  <th className={TABLE_HEAD_CLASS}>Status</th>
                  {showCoeColumn ? (
                    <th className={TABLE_HEAD_CLASS}>COE Reached At</th>
                  ) : null}
                  <th className={`${TABLE_HEAD_CLASS} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colSpan} className={`${TABLE_CELL_CLASS} py-8 text-center text-gray-500`}>
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className={`${TABLE_CELL_CLASS} py-8 text-center text-gray-500`}>
                      No applications found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.application_id} className="border-b border-gray-100 hover:bg-gray-50/80">
                      <td className={`${TABLE_CELL_CLASS} tabular-nums`}>
                        <TruncateTooltip
                          text={row.application_id}
                          className="font-mono text-[12px] font-semibold text-[#007BFF]"
                        />
                      </td>
                      <td className={`${TABLE_CELL_CLASS} max-w-[200px]`}>
                        <TruncateTooltip
                          text={row.student?.name}
                          className="block text-[13px] font-semibold text-gray-900"
                        />
                        <TruncateTooltip
                          text={row.student?.email}
                          className="block text-[11px] text-gray-500"
                        />
                        <TruncateTooltip
                          text={row.student?.mobile}
                          className="block text-[11px] text-gray-500"
                        />
                      </td>
                      <td className={`${TABLE_CELL_CLASS} max-w-[140px]`}>
                        <TruncateTooltip text={row.intake} className="block text-[13px]" />
                      </td>
                      <td className={TABLE_CELL_CLASS}>
                        {row.reached_coe ? (
                          <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                            Enrolled (COE)
                          </span>
                        ) : (
                          <span
                            className="inline-flex max-w-[140px] rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold leading-snug text-slate-600"
                            title="No COE/CAS/LOA in status history — conditional offer alone does not count"
                          >
                            Not enrolled
                          </span>
                        )}
                      </td>
                      <td className={`${TABLE_CELL_CLASS} overflow-visible`}>
                        <ProviderInsightsApplicationStatus
                          applicationId={row.application_id}
                          currentStatus={row.current_status}
                          statusHistory={row.status_history ?? row.statusHistory}
                        />
                      </td>
                      {showCoeColumn ? (
                        <td className={TABLE_CELL_CLASS}>
                          <TruncateTooltip text={formatDate(row.coe_reached_at)} className="text-gray-500" />
                        </td>
                      ) : null}
                      <td className={`${TABLE_CELL_CLASS} text-right`}>
                        <Link
                          to={`/applications/view-education-application-detail/${row.application_id}`}
                          className="inline-flex items-center gap-0.5 text-[13px] font-medium text-[#007BFF] hover:underline"
                        >
                          View
                          <FaChevronRight size={10} />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 ? (
            <div className="shrink-0 border-t border-gray-200 px-2 py-2">
              <Paginations
                currentPage={pagination.currentPage}
                totalItems={pagination.total}
                itemsPerPage={pagination.perPage}
                lastPage={pagination.lastPage}
                perItem={rows.length}
                onPageChange={handlePageChange}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
